import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SearchIcon from "@mui/icons-material/Search";
import logoTradeview from "../../assets/logo_tradeview_white.svg";
import * as styles from "./Styles";

const categories = ["Explore", "Banners", "Brand videos", "Decks", "Printed marketing", "Logos"];

const sections = [
  {
    title: "Banners",
    items: [
      {
        title: "Generic banners / Folder",
        details: [
          ["Total files", "3"],
          ["Size", "600x320px, 300x300px, 320x120px"],
          ["Language", "English"],
        ],
      },
      {
        title: "Tradeview-banner-logo.jpg",
        details: [
          ["Language", "English"],
          ["Size", "600x320px"],
        ],
      },
      {
        title: "Tradeview-banner-logo.jpg",
        details: [
          ["Language", "English"],
          ["Size", "1200x620px"],
        ],
      },
    ],
  },
  {
    title: "Brand videos",
    items: Array.from({ length: 3 }, () => ({
      title: "Tradeview-banner-logo.jpg",
      details: [
        ["Language", "English"],
        ["Size", "600x320px"],
        ["Duration", "2:56min"],
      ],
    })),
  },
  {
    title: "Decks",
    items: Array.from({ length: 3 }, () => ({
      title: "Tradeview-banner-logo.jpg",
      details: [
        ["Language", "English"],
        ["Size", "600x320px"],
      ],
    })),
  },
  {
    title: "Logos",
    items: Array.from({ length: 3 }, (_, index) => ({
      title: index === 0 ? "Tradeview primary logo" : "Tradeview-logo-package.zip",
      details: [
        ["Language", "English"],
        ["Format", index === 0 ? "SVG, PNG" : "SVG, PNG, EPS"],
      ],
    })),
  },
];

function ResourcePreview() {
  return (
    <Box sx={styles.preview}>
      <IconButton aria-label="Open preview" sx={styles.previewButton}>
        <OpenInFullIcon sx={{ fontSize: 14 }} />
      </IconButton>
      <img src={logoTradeview} alt="Tradeview Markets" />
    </Box>
  );
}

function ResourceCard({ item, cardId, selected, onSelect }) {
  return (
    <Box sx={styles.card}>
      <ResourcePreview />
      <Typography sx={styles.cardTitle}>{item.title}</Typography>
      <Box sx={styles.details}>
        {item.details.map(([label, value]) => (
          <Box key={label} sx={styles.detailRow}>
            <Typography component="span" sx={styles.detailLabel}>
              {label}:
            </Typography>
            <Typography component="span" sx={styles.detailValue}>
              {value}
            </Typography>
          </Box>
        ))}
        <Box sx={styles.detailRow}>
          <Typography component="span" sx={styles.detailLabel}>
            Category:
          </Typography>
          <Box sx={styles.tags}>
            <Box component="span" sx={styles.tag}>Logo</Box>
            <Box component="span" sx={styles.tag}>Banner</Box>
          </Box>
        </Box>
      </Box>
      <Box sx={styles.cardActions}>
        <Box sx={styles.selectLater}>
          <Checkbox
            checked={selected}
            onChange={() => onSelect(cardId)}
            size="small"
            sx={styles.checkbox}
            inputProps={{ "aria-label": `Select ${item.title} for download later` }}
          />
          <Typography sx={styles.selectText}>Select for download later</Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadOutlinedIcon />}
          sx={styles.downloadButton}
        >
          Download
        </Button>
      </Box>
    </Box>
  );
}

function ResourceSection({ section, sectionIndex, selected, onSelect }) {
  return (
    <Box sx={styles.section}>
      <Box sx={styles.sectionHeader}>
        <Typography sx={styles.sectionTitle}>{section.title}</Typography>
        <Button
          variant="outlined"
          endIcon={<span className="material-symbols-outlined">north_east</span>}
          sx={styles.seeAllButton}
        >
          See all
        </Button>
      </Box>
      <Box sx={styles.cardGrid}>
        {section.items.map((item, itemIndex) => {
          const cardId = `${sectionIndex}-${itemIndex}`;
          return (
            <ResourceCard
              key={cardId}
              item={item}
              cardId={cardId}
              selected={selected.includes(cardId)}
              onSelect={onSelect}
            />
          );
        })}
      </Box>
    </Box>
  );
}

function MarketingResourcesPage() {
  const [activeCategory, setActiveCategory] = useState("Explore");
  const [search, setSearch] = useState("");
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [selected, setSelected] = useState([]);

  const visibleSections = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return sections
      .filter((section) => activeCategory === "Explore" || section.title === activeCategory)
      .map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          item.title.toLowerCase().includes(normalizedSearch)
        ),
      }))
      .filter((section) => section.items.length > 0);
  }, [activeCategory, search]);

  const handleSelect = (cardId) => {
    setSelected((current) =>
      current.includes(cardId)
        ? current.filter((selectedId) => selectedId !== cardId)
        : [...current, cardId]
    );
  };

  return (
    <Box sx={styles.page}>
      <Box sx={styles.pageHeader}>
        <Typography component="h1" sx={styles.title}>
          Our content resources
        </Typography>
        <Typography sx={styles.subtitle}>
          Access customizable marketing templates to build a strong communication with your client-base
        </Typography>
      </Box>

      {showDisclaimer && (
        <Box sx={styles.disclaimer}>
          <InfoOutlinedIcon sx={styles.infoIcon} />
          <Box sx={styles.disclaimerContent}>
            <Typography sx={styles.disclaimerTitle}>Disclaimer</Typography>
            <Typography sx={styles.disclaimerText}>
              Any reference to Tradeview Markets or use of its assets (including images, logos, copy,
              graphic materials, etc.) must strictly adhere to the company's{" "}
              <Link href="#" sx={styles.disclaimerLink}>
                IB Marketing and branding policies
              </Link>
              . It is not permitted to modify, adapt, or combine these materials with third-party logos
              without prior written authorization from the Compliance team and the Global Branding
              department. If you wish to create additional content, incorporate other visual elements,
              include partner brands, or adapt the message for local use, you must submit a formal request
              and obtain the corresponding approval.
            </Typography>
          </Box>
          <IconButton
            aria-label="Close disclaimer"
            onClick={() => setShowDisclaimer(false)}
            sx={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      <Box sx={styles.toolbar}>
        <Box sx={styles.tabs}>
          {categories.map((category) => (
            <Button
              key={category}
              onClick={() => setActiveCategory(category)}
              sx={styles.tab(activeCategory === category)}
            >
              {category}
            </Button>
          ))}
        </Box>
        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Filter by keyword"
          size="small"
          sx={styles.search}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: "#4f657b" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={styles.sections}>
        {visibleSections.map((section, sectionIndex) => (
          <ResourceSection
            key={section.title}
            section={section}
            sectionIndex={sectionIndex}
            selected={selected}
            onSelect={handleSelect}
          />
        ))}
        {visibleSections.length === 0 && (
          <Box sx={styles.emptyState}>
            <SearchIcon sx={{ fontSize: 32 }} />
            <Typography>No resources match your search.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default MarketingResourcesPage;
